/**
 * Log Sanitization Utilities
 *
 * SECURITY: VULN-008 FIX
 * Provides utilities for sanitizing sensitive data before logging.
 * Prevents exposure of PII (Personally Identifiable Information) and tokens in logs.
 *
 * Usage:
 * - Use hashIdentifier() for usernames, emails, or other PII
 * - Never log: tokens, passwords, decoded JWT payloads, full email addresses
 * - Use allowlist approach: only log non-sensitive metadata
 *
 * GDPR Compliance:
 * - Hashed identifiers are not reversible (one-way hash)
 * - Allows debugging without exposing user identity
 * - Complies with data minimization principle
 */

/**
 * Hash a sensitive identifier for safe logging
 *
 * Uses a simple but fast hash function (djb2) to create a consistent
 * identifier hash that can be used for debugging without exposing PII.
 *
 * Example:
 *   hashIdentifier('john.doe@example.com') => 'user_a3f2b1c9'
 *   hashIdentifier('john.doe') => 'user_7d8e4f2a'
 *
 * @param {string} identifier - The sensitive identifier (username, email, etc.)
 * @returns {string} A hashed identifier prefixed with 'user_'
 */
export function hashIdentifier(identifier: string): string {
  if (!identifier) {
    return 'user_unknown';
  }

  // Simple djb2 hash algorithm (fast and sufficient for logging purposes)
  let hash = 5381;
  for (let i = 0; i < identifier.length; i++) {
    hash = ((hash << 5) + hash) + identifier.charCodeAt(i); // hash * 33 + char
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to unsigned 32-bit integer and format as hex
  const hashValue = (hash >>> 0).toString(16).padStart(8, '0');

  return `user_${hashValue}`;
}

/**
 * Redact email address for safe logging
 *
 * Masks the username portion of an email while keeping the domain visible.
 * Useful when domain information is needed for debugging.
 *
 * Example:
 *   redactEmail('john.doe@example.com') => 'j***@example.com'
 *   redactEmail('a@test.com') => 'a***@test.com'
 *
 * @param {string} email - The email address to redact
 * @returns {string} Redacted email address
 */
export function redactEmail(email: string): string {
  if (!email || !email.includes('@')) {
    return '[redacted]';
  }

  const [username, domain] = email.split('@');

  // Keep first character, redact the rest
  const redactedUsername = username.length > 0
    ? username[0] + '***'
    : '***';

  return `${redactedUsername}@${domain}`;
}

/**
 * Get safe user metadata for logging
 *
 * Extracts only non-sensitive metadata from user objects for logging.
 * Excludes PII like email, username, full name.
 *
 * @param {Record<string, unknown>} user - User object with potential PII
 * @returns {Record<string, unknown>} Safe metadata for logging
 */
export function getSafeUserMetadata(user: Record<string, unknown>): Record<string, unknown> {
  const username = user.username as string | undefined;
  const email = user.email as string | undefined;

  return {
    userId: user.id || user.user_id,
    userHash: username ? hashIdentifier(username) : undefined,
    emailDomain: email?.split('@')[1], // Keep domain, drop username
    role: user.role,
    organizationId: user.organization_id,
    hasEmail: Boolean(email),
    hasUsername: Boolean(username),
    // DO NOT include: username, email, full_name, phone, etc.
  };
}

/**
 * Check if a log value might contain sensitive data
 *
 * Returns true if the value appears to be sensitive and should not be logged.
 * Use as a safety check before logging dynamic values.
 *
 * @param {string} value - Value to check
 * @returns {boolean} True if value appears sensitive
 */
export function isSensitiveValue(value: string): boolean {
  if (!value || value.length === 0) {
    return false;
  }

  // Check for JWT token patterns (three base64 segments separated by dots)
  if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) {
    return true;
  }

  // Check for bearer tokens
  if (value.toLowerCase().startsWith('bearer ')) {
    return true;
  }

  // Check for very long base64-like strings (likely tokens)
  if (value.length > 50 && /^[A-Za-z0-9+/=_-]+$/.test(value)) {
    return true;
  }

  // Check for email addresses
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return true;
  }

  return false;
}

/**
 * Sanitize an object for safe logging
 *
 * Recursively scans an object and redacts any sensitive-looking values.
 * Use as a safety net when logging complex objects.
 *
 * @param {Record<string, unknown>} obj - Object to sanitize
 * @returns {Record<string, unknown>} Sanitized object
 */
export function sanitizeForLogging(obj: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Redact known sensitive keys
    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes('token') ||
      lowerKey.includes('password') ||
      lowerKey.includes('secret') ||
      lowerKey.includes('key') ||
      lowerKey === 'authorization'
    ) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Check string values for sensitive patterns
    if (typeof value === 'string') {
      if (isSensitiveValue(value)) {
        sanitized[key] = '[REDACTED]';
      } else if (value.includes('@')) {
        // Might be an email
        sanitized[key] = redactEmail(value);
      } else {
        sanitized[key] = value;
      }
      continue;
    }

    // Recursively sanitize nested objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeForLogging(value as Record<string, unknown>);
      continue;
    }

    // Arrays and other types pass through
    sanitized[key] = value;
  }

  return sanitized;
}
